import type {
  FramingAttemptSummary,
  FramingCoordinateSpace,
  FramingStageKey,
} from "../framingInspector";

/**
 * Attempt comparison support (CROP-WB-011): sanitized field deltas between
 * bounded attempt summaries, plus geometric comparison eligibility. Wipe
 * comparison is allowed only for the exact same stage with equal dimensions;
 * everything else stays side-by-side with independent viewports.
 */

export type AttemptFieldDelta = {
  field: string;
  a: string | null;
  b: string | null;
  changed: boolean;
};

export function diffAttemptSummaries(
  a: FramingAttemptSummary,
  b: FramingAttemptSummary,
): AttemptFieldDelta[] {
  const fields: Array<[string, (item: FramingAttemptSummary) => unknown]> = [
    ["status", (item) => item.status],
    ["ordinal", (item) => item.ordinal],
    ["started_at", (item) => item.startedAt],
    ["completed_at", (item) => item.completedAt],
    ["plan_digest", (item) => item.planDigest],
    ["receipt_digest", (item) => item.receiptDigest],
    ["report_digest", (item) => item.reportDigest],
    ["evidence_availability", (item) => item.evidenceAvailability],
  ];
  return fields.map(([field, read]) => {
    const left = read(a) == null ? null : String(read(a));
    const right = read(b) == null ? null : String(read(b));
    return { field, a: left, b: right, changed: left !== right };
  });
}

export type ComparisonEligibility = {
  sideBySide: true;
  wipe: boolean;
  reason: string | null;
};

export function comparisonEligibility(params: {
  stageA: FramingStageKey | null;
  stageB: FramingStageKey | null;
  spaceA: FramingCoordinateSpace | null;
  spaceB: FramingCoordinateSpace | null;
  durationA?: number | null;
  durationB?: number | null;
}): ComparisonEligibility {
  const { stageA, stageB, spaceA, spaceB } = params;
  if (!stageA || !stageB || stageA !== stageB) {
    return {
      sideBySide: true,
      wipe: false,
      reason: "Attempts must be compared at one identical stage.",
    };
  }
  if (!spaceA || !spaceB) {
    return {
      sideBySide: true,
      wipe: false,
      reason: "One attempt has no recorded geometry for this stage.",
    };
  }
  if (
    !spaceA.width ||
    !spaceA.height ||
    spaceA.width !== spaceB.width ||
    spaceA.height !== spaceB.height
  ) {
    return {
      sideBySide: true,
      wipe: false,
      reason:
        "Stage dimensions differ; the pixels are not geometrically comparable.",
    };
  }
  const durationA = params.durationA ?? null;
  const durationB = params.durationB ?? null;
  if (
    durationA != null &&
    durationB != null &&
    Math.abs(durationA - durationB) > 1 / 30
  ) {
    return {
      sideBySide: true,
      wipe: false,
      reason:
        "Attempt durations differ; compare side-by-side with independent time.",
    };
  }
  return { sideBySide: true, wipe: true, reason: null };
}
